/**
 * Nav-tab visibility predicates for the go-live reveal gates.
 *
 * Extracted from the route configs so the rule is unit-testable without
 * importing a whole route tree, and so the flags can never drift apart in how
 * they are parsed. Every flag uses the SAME `=== "true"` comparison the
 * original inline constants used — an absent variable (production, today) is
 * false, i.e. the tab stays hidden.
 *
 *  - VITE_STAGE2_TABS_UNLOCK
 *      Staging-wide Stage-2 reveal. Set to "true" only on the FE Vercel
 *      Preview env. Covers the teacher "grading", "grade book", "extensions"
 *      and "course management" tabs.
 *
 *  - VITE_TEACHER_GRADEBOOK_UNLOCK
 *      NARROW gate added by the 2026-09-21 Train-0 hotfix so PRODUCTION can
 *      ship the teacher "grading" + "grade book" tabs on their own, without
 *      also revealing the unfinished extensions / course-management tabs that
 *      flipping STAGE2 would drag along.
 *
 * Both are read from `import.meta.env`, which Vite inlines at BUILD time — the
 * variable must exist in the Vercel environment BEFORE the build runs.
 */
export interface TabFlagEnv {
  VITE_STAGE2_TABS_UNLOCK?: string;
  VITE_TEACHER_GRADEBOOK_UNLOCK?: string;
  VITE_ADMIN_STUDENT_COURSES?: string;
}

export const isStage2Unlocked = (env: TabFlagEnv): boolean =>
  env.VITE_STAGE2_TABS_UNLOCK === "true";

export const isTeacherGradebookUnlocked = (env: TabFlagEnv): boolean =>
  env.VITE_TEACHER_GRADEBOOK_UNLOCK === "true";

/** `hidden` for a nav tab gated by STAGE2 alone. */
export const isStage2TabHidden = (env: TabFlagEnv): boolean => !isStage2Unlocked(env);

/**
 * `hidden` for the teacher grading / grade-book tabs — EITHER flag reveals
 * them, so staging keeps its current behaviour while production can unlock
 * just these two.
 */
export const isTeacherGradebookTabHidden = (env: TabFlagEnv): boolean =>
  !(isStage2Unlocked(env) || isTeacherGradebookUnlocked(env));

export const isAdminStudentCoursesEnabled = (env: TabFlagEnv): boolean =>
  env.VITE_ADMIN_STUDENT_COURSES === "true";
